
import { Question } from '@/types/assessment';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';

interface SliderQuestionProps {
  question: Question;
  currentValue: number;
  targetValue: number;
  onChange: (current: number, target: number) => void;
}

const SliderQuestion = ({ 
  question, 
  currentValue, 
  targetValue, 
  onChange 
}: SliderQuestionProps) => {
  // Determine min and max from answers or use defaults
  const minValue = question.answers.length > 0 ? 1 : 1;
  const maxValue = question.answers.length > 0 ? question.answers.length : 5;
  
  const handleCurrentChange = (value: number[]) => {
    onChange(value[0], targetValue);
  };
  
  const handleTargetChange = (value: number[]) => {
    onChange(currentValue, value[0]);
  };

  // Get label for a specific value
  const getValueLabel = (value: number) => {
    const answer = question.answers.find(a => a.value === value);
    return answer ? answer.text : `Level ${value}`;
  };

  return (
    <div className="space-y-8">
      <div className="p-4 border rounded-lg bg-gray-50">
        <div className="mb-4">
          <h3 className="font-medium text-gray-700">Current State</h3>
          <p className="text-sm text-muted-foreground">Select your current position</p>
        </div>
        
        <div className="space-y-6 px-2">
          <Slider 
            value={[currentValue]} 
            min={minValue} 
            max={maxValue} 
            step={1} 
            onValueChange={handleCurrentChange} 
          />
          
          <div className="flex justify-between text-xs text-muted-foreground">
            {Array.from({ length: maxValue - minValue + 1 }, (_, i) => i + minValue).map(value => (
              <div key={value} className="text-center px-2">
                <div className="mb-1">{value}</div>
                <div>{getValueLabel(value)}</div>
              </div>
            ))}
          </div>
          
          <div className="text-center font-medium">
            Current: {getValueLabel(currentValue)}
          </div>
        </div>
      </div>
      
      <div className="p-4 border rounded-lg bg-[#fef9f5]">
        <div className="mb-4">
          <h3 className="font-medium text-[#e87a2e]">Target State</h3>
          <p className="text-sm text-muted-foreground">Select your desired future position</p>
        </div>
        
        <div className="space-y-6 px-2">
          <Slider 
            value={[targetValue]} 
            min={minValue} 
            max={maxValue} 
            step={1} 
            onValueChange={handleTargetChange} 
            className="bg-[#e87a2e]/20" 
          />
          
          <div className="flex justify-between text-xs text-muted-foreground">
            {Array.from({ length: maxValue - minValue + 1 }, (_, i) => i + minValue).map(value => (
              <div key={value} className="text-center px-2">
                <div className="mb-1">{value}</div>
                <div>{getValueLabel(value)}</div>
              </div>
            ))}
          </div>
          
          <div className="text-center font-medium text-[#e87a2e]">
            Target: {getValueLabel(targetValue)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SliderQuestion;
